<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { mx_$pt } from '@/utils/helpers'
import { useNotificationStore } from '@/stores/notifications/store'
import { selectNotification } from '@/stores/notifications/utils'
import { useTariffsStore } from '@/stores/tariffs/store'
import { useCtaStore } from '@/stores/cta/store'
import { useCoreStore } from '@/stores/core/store'
import { useNetworkStore } from '@/stores/network/store'
import ConfirmationPlanPage from '@/components/Base/ConfirmationPlanPage.vue'
import Spinner from '@/components/Base/Spinner.vue'
import CaretDownIcon from '@/components/Base/Icons/CaretDownIcon.vue'
import ModalBackdrop from '@/components/Base/Modal/ModalBackdrop.vue'

const emit = defineEmits<{
  (e: 'close'): void
}>()

const props = defineProps<{
  preselectEdition?: string
}>()

const i18n = useI18n()

const router = useRouter()

const notificationStore = useNotificationStore()

const tariffsStore = useTariffsStore()

const ctaStore = useCtaStore()

const coreStore = useCoreStore()

const networkStore = useNetworkStore()

const page = ref('selectPlan')

const isLoading = computed(
  () => tariffsStore.meta.possibleTariffs.status === 'pending'
)

const selectedNewSeats = ref(10)

const selectedNewEdition = ref('standard')

const saveDisabled = ref(false)

const isSaving = ref(false)

const plans = computed(() => {
  return {
    current: createPlan(ctaStore.get.current),
    downgrade: createPlan(ctaStore.get.downgrade),
    upgrade: createPlan(ctaStore.get.upgrade),
  }
})

const currentPlan = computed(() => {
  return tariffsStore.possibleTariffs.find((element) => {
    return element.isCurrent
  })
})

const newPlan = computed(() => {
  return tariffsStore.possibleTariffs.find((element) => {
    return (
      element.seats === selectedNewSeats.value &&
      element.edition === selectedNewEdition.value
    )
  })
})

const ctaResetNecessary = computed(() => {
  return (
    currentPlanEdition.value !== 'basic' && selectedNewEdition.value === 'basic'
  )
})

const isOldPlan = computed(() => {
  if (!currentPlan.value) {
    return false
  }

  if (!currentPlan.value?.edition) {
    return true
  }

  return false
})

const currentPlanName = computed(() => {
  if (!currentPlan.value) {
    return ''
  }

  if (!currentPlan.value?.edition) {
    return i18n.t(
      'settings.plan.' + currentPlan.value.productCode + '.description'
    )
  }

  return (
    i18n.t('settings.tariff.name.edition.' + currentPlan.value?.edition) +
    ' - ' +
    currentPlan.value?.seats
  )
})

const currentPlanPrice = computed(() => {
  return currentPlan.value?.price?.replace('.', ',')
})

const currentPlanSeats = computed(() => {
  return currentPlan.value?.seats ?? 10
})

const currentPlanEdition = computed(() => {
  return currentPlan.value?.edition ?? 'standard'
})

const newPlanName = computed(() => {
  return (
    i18n.t('settings.tariff.name.edition.' + newPlan.value?.edition) +
    ' - ' +
    newPlan.value?.seats
  )
})

const newPlanSeats = computed(() => {
  return newPlan.value?.seats
})

const newPlanPrice = computed(() => {
  return newPlan.value?.price?.replace('.', ',')
})

const newPlanCode = computed(() => {
  return newPlan.value?.productCode
})

const possibleSeats = computed(() => {
  if (!tariffsStore.possibleTariffs) {
    return []
  }

  const filterredTariffs = tariffsStore.possibleTariffs
    .filter((tariff) => {
      return selectedNewEdition.value === tariff.edition
    })
    .map((tariff) => {
      return tariff.seats
    })

  const uniqueItems = filterredTariffs
    .filter((value, index, array) => array.indexOf(value) === index)
    .sort((a, b) => a - b)

  return uniqueItems
})

const possibleEditions = computed(() => {
  if (!tariffsStore.possibleTariffs) {
    return []
  }

  const filterredTariffs = tariffsStore.possibleTariffs
    .map((tariff) => {
      return tariff.edition
    })
    .filter((e) => e)

  const uniqueItems = filterredTariffs.filter(
    (value, index, array) => array.indexOf(value) === index
  )

  return uniqueItems
})

const plansAvailable = computed(() => {
  return plans.value.downgrade !== null || plans.value.upgrade !== null
})

const selectedPlanData = computed(() => {
  return createPlan(newPlanCode.value)
})

watch(
  () => selectedNewEdition.value,
  () => {
    saveDisabled.value = false

    if (!possibleSeats.value.includes(selectedNewSeats.value)) {
      selectedNewSeats.value = possibleSeats.value[0]
    }
  }
)

watch(
  () => selectedNewSeats.value,
  () => {
    saveDisabled.value = false
  }
)

onBeforeMount(async () => {
  await tariffsStore.loadTariffs()

  selectedNewEdition.value = props.preselectEdition ?? currentPlanEdition.value

  selectedNewSeats.value = currentPlanSeats.value
})

async function buyPlan() {
  try {
    isSaving.value = true


    await networkStore.updatePlan({ tariff: newPlanCode.value })
    await coreStore.reloadAll()

    notificationStore.add({
      type: 'success',
      title: computed(() =>
        i18n.t('settings.plan.planChangeSuccess', {
          label: newPlanName.value,
        })
      ),
    })

    router.push('/')
  } catch (error: any) {
    if (error.response.status === 422) {
      getErrorMessages422(error)
    } else if (error.response.status === 409) {
      getErrorMessages409(error)
    } else {
      notificationStore.add(selectNotification(error))
    }
  }
  isSaving.value = false
}

function editionName(edition: string) {
  edition = edition === 'standard' ? 'classic' : edition

  return `${edition.charAt(0).toUpperCase()}${edition.slice(1)}`
}

function getErrorMessages422(error: any) {
  Object.entries(error.response?.data?.errors).forEach(([key]) => {
    notificationStore.add({
      type: 'error',
      title: computed(
        () =>
          mx_$pt('settings.tariff.modal.error.can_not_downgrade.count', {
            count: newPlanSeats.value,
            type: i18n.t(`global.common.${key}`),
          }).value
      ),
    })
  })
}

function getErrorMessages409(error: any) {
  error.response.data.reason.forEach((reason: string) => {
    notificationStore.add({
      type: 'error',
      title: computed(() =>
        i18n.t(
          `settings.tariff.modal.error.${error.response.data.code.toLowerCase()}.${reason.toLowerCase()}`
        )
      ),
    })
  })
}

function close() {
  emit('close')
}

function backToSelectPlanPage() {
  page.value = 'selectPlan'
}

function choosePlan() {
  page.value = 'confirmationPage'
}

function createPlan(shortcode?: string) {
  if (!shortcode) {
    return
  }

  return {
    shortcode,
    name: newPlanName.value,
    description: mx_$pt('settings.tariff.modal.confirm.product', {
      newPlanSeats: newPlanSeats.value,
      newPlanPrice: newPlanPrice.value,
    }).value,
  }
}
</script>

<template>
  <ModalBackdrop>
    <div
      class="flex flex-col justify-between static z-20 shadow-md bg-neutral-0 w-full md:max-w-[434px] h-full md:h-auto md:rounded-lg"
      data-test="plan-change-modal"
    >
      <div v-if="isLoading" class="p-2 m-2 rounded flex items-center">
        <Spinner class="flex-1" />
      </div>

      <div v-if="!isLoading" class="p-4">
        <h4 class="eb-heading mb-4 text-neutral-700">
          {{ $t('settings.plan.modal.changePbxSize') }}
        </h4>

        <div v-if="page === 'selectPlan'" data-test="select-plan">
          <h5 class="text-md eb-heading mt-5 mb-2">
            {{ $t('settings.tariff.modal.title.edition') }}
          </h5>

          <div class="mb-4 text-sm text-neutral-700">
            {{ $t('settings.tariff.modal.edition') }}
          </div>

          <div class="relative flex items-center py-1 w-full mb-4">
            <select
              id="edition"
              v-model="selectedNewEdition"
              class="eb-select-legacy"
              data-test="edition-select"
            >
              <option
                v-for="option in possibleEditions"
                :key="`${option}`"
                :value="option"
              >
                {{ editionName(option) }}
                {{ $t('settings.plan.modal.edition') }}
              </option>
            </select>

            <CaretDownIcon class="eb-icon" />
          </div>

          <h5 class="text-md eb-heading mb-2">
            {{ $t('settings.tariff.modal.title.extensions') }}
          </h5>

          <div class="mb-4 text-sm text-neutral-700">
            {{ $t('settings.tariff.modal.seats') }}
          </div>

          <div class="relative flex items-center py-1 w-full">
            <select
              id="seats"
              v-model="selectedNewSeats"
              class="eb-select-legacy"
              data-test="seats-select"
            >
              <option
                v-for="option in possibleSeats"
                :key="`${option}`"
                :value="option"
              >
                {{ option }} {{ $t('settings.plan.modal.seats') }}
              </option>
            </select>

            <CaretDownIcon class="eb-icon" />
          </div>

          <div v-if="plansAvailable">
            <h5 class="text-md eb-heading mt-4 mb-2">
              {{ $t('settings.plan.modal.newPlan') }}
            </h5>

            <div
              v-if="!isOldPlan"
              class="mb-4 text-sm text-neutral-700"
              v-html="
                mx_$pt('settings.tariff.modal.currentPlan', {
                  currentPlan: currentPlanName,
                  currentPrice: currentPlanPrice,
                  newPlan: newPlanName,
                  newPlanPrice: newPlanPrice,
                }).value
              "
            ></div>

            <div
              v-if="isOldPlan"
              class="mb-4 text-sm text-neutral-700"
              v-html="
                mx_$pt('settings.tariff.modal.currentPlanOld', {
                  currentPlan: currentPlanName,
                  currentPrice: currentPlanPrice,
                  newPlan: newPlanName,
                  newPlanPrice: newPlanPrice,
                }).value
              "
            ></div>
          </div>

          <div class="flex justify-end gap-4 pt-4">
            <button
              class="inline-flex eb-legacy-button eb-legacy-button--md eb-legacy-button--secondary"
              @click="close"
            >
              {{ $t('global.action.cancel') }}
            </button>

            <button
              data-test="plan-upgrade"
              class="inline-flex eb-legacy-button eb-legacy-button--md eb-legacy-button--primary"
              :disabled="newPlanCode === '' || newPlan.isCurrent"
              @click="choosePlan"
            >
              {{ $t('global.common.choose') }}
            </button>
          </div>
        </div>

        <ConfirmationPlanPage
          v-if="page === 'confirmationPage'"
          page="confirmationPage"
          :button-disabled="saveDisabled"
          :is-saving="isSaving"
          data-test="confirmation-page"
          :selected-plan-data="selectedPlanData"
          :cta-reset-necessary="ctaResetNecessary"
          @buyPlan="buyPlan"
          @backToSelectPlanPage="backToSelectPlanPage"
        />
      </div>
    </div>
  </ModalBackdrop>
</template>
